import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Play } from "lucide-react";
import { CTAButton, Container, Section, GlassCard, Eyebrow, H2, Lead, Orbs, CompareTable } from "../components/PageUI";
import { coreModules, homeModules, hardware, compareRows, faqs, brand } from "../mock";
import { VisaLogo, MastercardLogo, AmexLogo, ApplePayLogo, GooglePayLogo, NfcLogo, AchLogo, PayChip } from "../components/PaymentLogos";
import SEO from "../components/SEO";

export default function Home() {
  return (
    <div>
      <SEO
        title="Masjid Suite | Free Mosque Management Software & Donation Kiosks"
        description="Free masjid management software for donations, Zakat, events, and announcements, plus donation kiosks and card readers you can buy or rent. No subscription, ever."
        path="/"
      />
      <HeroSection />
      <TrustBar />
      <PaymentsBar />
      <IncludedSection />
      <HardwareTeaser />
      <WhyBuiltSection />
      <FaqTeaser />
      <FinalCTA />
    </div>
  );
}

function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const rotateX = useTransform(scrollYProgress, [0, 1], [0, 20]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.88]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const smoothRotate = useSpring(rotateX, { stiffness: 60, damping: 20 });

  return (
    <section ref={ref} className="relative min-h-[100vh] bg-white overflow-hidden pt-24">
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-[65fr_45fr] gap-6 lg:gap-10 items-center">
        <motion.div style={{ opacity }} className="space-y-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="inline-flex items-center gap-2 rounded-full glass-frosted px-4 py-1.5 text-[11px] tracking-widest text-[#163410] font-medium uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-[#c9a34a]" />
            Free Software · Honest Hardware · Built Only for Masjid
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }} className="font-display font-tight text-5xl md:text-6xl lg:text-[80px] text-[#1d1d1f] leading-[0.98] font-semibold">
            Modern Technology
            <span className="block text-gold-shine">for Modern Masjids.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="text-[#4b5563] text-lg md:text-xl max-w-xl leading-[1.5]">
            Masjid Suite empowers masjids to serve their communities with simplicity, security, and innovation. A comprehensive, secure platform designed to help modern masjids operate more efficiently… and it’s <span className="text-gold-shine font-semibold">completely free</span>.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="text-[#1d1d1f] font-medium">
            Focus on your mission. Let Masjid Suite handle the technology.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="flex flex-wrap gap-3 pt-2">
            <CTAButton to="/contact">Get Started Free</CTAButton>
            <CTAButton to="/software" variant="secondary">See What’s Free</CTAButton>
          </motion.div>
        </motion.div>

        <motion.div style={{ y, scale, rotateX: smoothRotate }} className="perspective-2000 flex justify-center">
          <HeroCard />
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#163410]/60 text-[10px] tracking-[0.3em] uppercase">
        <span>Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }} className="w-px h-8 bg-gradient-to-b from-[#0e2408]/60 to-transparent" />
      </motion.div>
    </section>
  );
}

function HeroCard() {
  return (
    <div className="relative preserve-3d w-full">
      {/* subtle ground shadow only */}
      <div className="absolute -bottom-2 left-16 right-16 h-8 rounded-full bg-black/15 blur-2xl -z-10" />

      <motion.img
        src="/assets/hero-devices.png"
        alt="Masjid Suite kiosk and mobile app in action"
        className="block w-full h-auto object-contain select-none"
        draggable={false}
        style={{ filter: "drop-shadow(0 30px 40px rgba(14,36,8,0.12))" }}
        animate={{ y: [0, -14, 0], rotateY: [-4, 4, -4], rotateX: [1, -1, 1] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

function TrustBar() {
  return (
    <div className="bg-hero-light border-y border-[#e6e7eb] py-5">
      <Container>
        <div className="flex flex-wrap items-center justify-center gap-2.5 text-[14px] text-[#1d1d1f]">
          <Icons.Globe size={16} className="text-[#a8862f]" />
          <span>Over <span className="text-gold-shine font-semibold">4,000 masjids</span> on our platform, from around the world.</span>
        </div>
      </Container>
    </div>
  );
}

function PaymentsBar() {
  return (
    <div className="bg-section-alt border-y border-[#e6e7eb] py-6">
      <Container>
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-[13px] text-[#4b5563]">
          <span>Payments processed securely through <span className="font-semibold text-[#1d1d1f]">Stripe</span>. We accept:</span>
          <div className="flex flex-wrap items-center gap-2.5">
            <PayChip label="Visa"><VisaLogo /></PayChip>
            <PayChip label="Mastercard"><MastercardLogo /></PayChip>
            <PayChip label="American Express"><AmexLogo /></PayChip>
            <PayChip label="Apple Pay"><ApplePayLogo /></PayChip>
            <PayChip label="Google Pay"><GooglePayLogo /></PayChip>
            <PayChip label="NFC / Tap to Pay"><NfcLogo /></PayChip>
            <PayChip label="ACH bank transfer"><AchLogo /></PayChip>
          </div>
        </div>
      </Container>
    </div>
  );
}

function IncludedSection() {
  return (
    <Section className="bg-hero-light overflow-hidden">
      <Orbs />
      <Container>
        <div className="text-center mb-16">
          <Eyebrow>What’s Included</Eyebrow>
          <H2 gradient="One platform.">Everything free.</H2>
          <div className="mt-6 flex justify-center"><Lead>Every module your masjid needs to manage donations, members, and community, all at no cost from day one.</Lead></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 perspective-2000">
          {homeModules.map((m, i) => {
            const Icon = Icons[m.icon] || Icons.Sparkles;
            return (
              <GlassCard key={i}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4 shadow-md"><Icon size={22} strokeWidth={1.8} /></div>
                <h3 className="font-display text-lg text-[#1d1d1f] font-semibold mb-2">{m.title}</h3>
                <p className="text-[14px] text-[#4b5563] leading-relaxed">{m.desc}</p>
              </GlassCard>
            );
          })}
        </div>
        <div className="mt-14 flex justify-center"><CTAButton to="/software">Explore All Features</CTAButton></div>
      </Container>
    </Section>
  );
}

function HardwareTeaser() {
  return (
    <Section className="bg-section-alt overflow-hidden">
      <Container>
        <div className="text-center mb-16">
          <Eyebrow>Hardware</Eyebrow>
          <H2 gradient="you can touch.">Only pay for what</H2>
          <div className="mt-6 flex justify-center"><Lead>Software stays free, choose only the hardware your masjid needs. You own your hardware, with no monthly software subscription.</Lead></div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {hardware.map((h, i) => {
            const Icon = Icons[h.icon] || Icons.Box;
            return (
              <GlassCard key={i}>
                <div className="aspect-square rounded-2xl bg-white flex items-center justify-center border border-[#e6e7eb] p-6 mb-5">
                  {h.image ? (
                    <img src={h.image} alt={h.name} className="max-w-full max-h-full object-contain" draggable={false} style={{ filter: "drop-shadow(0 10px 18px rgba(14,36,8,0.15))" }} />
                  ) : (
                    <Icon size={72} strokeWidth={1.4} className="text-[#0e2408]/70" />
                  )}
                </div>
                <h3 className="font-display text-lg text-[#1d1d1f] font-semibold mb-2">{h.name}</h3>
                <p className="text-[13px] text-[#4b5563] leading-relaxed">{h.desc}</p>
                {h.link && <Link to={h.link} className="mt-3 inline-flex items-center gap-1 text-[#0e2408] text-[13px] font-medium hover:gap-2 transition-all">{h.linkText}</Link>}
              </GlassCard>
            );
          })}
        </div>
        <div className="mt-14 flex justify-center"><CTAButton to="/pricing">View Hardware & Pricing</CTAButton></div>
      </Container>
    </Section>
  );
}

function WhyBuiltSection() {
  return (
    <Section className="bg-hero-light overflow-hidden">
      <Orbs />
      <Container>
        <div className="grid lg:grid-cols-2 gap-14 items-start mb-14">
          <div>
            <Eyebrow>Why Masjid Suite</Eyebrow>
            <H2 gradient="not bolted on.">Built only for masjid,</H2>
          </div>
          <Lead>We built this platform only for masjid, not as a generic nonprofit tool with mosque features added later. That focus lets us do two things most competitors can’t: give away the entire software suite for free, and price our hardware honestly, with no hidden monthly add-ons. The result is a platform that’s more user-friendly, secure and more affordable than the alternatives, without asking your masjid to compromise on either.</Lead>
        </div>
        <CompareTable rows={compareRows} />
        <div className="mt-12 flex justify-center"><CTAButton to="/why-us">Why Masjid Suite</CTAButton></div>
      </Container>
    </Section>
  );
}

function FaqTeaser() {
  return (
    <Section className="bg-section-alt overflow-hidden">
      <Container>
        <div className="text-center mb-14">
          <H2 gradient="Questions">Frequently Asked</H2>
          <div className="mt-6 flex justify-center"><Lead>Quick answers to common questions.</Lead></div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {faqs.map((f, i) => (
            <GlassCard key={i} hover={false}>
              <div className="text-[#0e2408] font-semibold text-[13px] uppercase tracking-widest mb-2">Q</div>
              <div className="font-display text-lg text-[#1d1d1f] font-semibold mb-3">{f.q}</div>
              <div className="text-[14px] text-[#4b5563] leading-relaxed">{f.a}</div>
            </GlassCard>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap justify-center gap-3">
          <CTAButton to="/faq" variant="secondary">View All FAQs</CTAButton>
          <CTAButton to="/contact">Contact Our Team</CTAButton>
        </div>
      </Container>
    </Section>
  );
}

function FinalCTA() {
  return (
    <Section className="bg-hero-light overflow-hidden">
      <Orbs />
      <Container>
        <div className="glass-frosted rounded-[40px] p-10 md:p-16 text-center shadow-apple-lg">
          <H2 gradient="and honest hardware pricing?">Ready for free masjid software</H2>
          <div className="mt-6 flex justify-center"><Lead>Get set up in a matter of days. No long-term contract, no sales pressure.</Lead></div>
          <div className="mt-10 flex justify-center"><CTAButton to="/contact">Get Started Free</CTAButton></div>
        </div>
      </Container>
    </Section>
  );
}
